import { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";
import axios from "axios";

const TempDataContext = createContext(null);

export const TempDataProvider = ({ children }) => {
    const [tempData, setTempData] = useState([]);
    const [version, setVersion] = useState(0); //  新增版本號

    // 新增項目或更新項目
    // compositing document_id + optional filter into a stable key so different filters are treated separately
    const stableStringify = useCallback((obj) => {
        if (obj === null) return 'null';
        if (typeof obj !== 'object') return JSON.stringify(obj);
        if (Array.isArray(obj)) return '[' + obj.map(stableStringify).join(',') + ']';
        const keys = Object.keys(obj).sort();
        return '{' + keys.map((k) => JSON.stringify(k) + ':' + stableStringify(obj[k])).join(',') + '}';
    }, []);

    const makeKey = useCallback((document_id, filter) => {
        const f = filter == null ? '' : stableStringify(filter);
        return `${document_id}::${f}`;
    }, [stableStringify]);

    const setItem = useCallback((document_id, response_data, filter = null) => {
        if (document_id == null) {
            console.warn('[useGlobalTempStore] setItem called with null/undefined document_id - ignored.');
            return;
        }
        setTempData((prev) => {
            const key = makeKey(document_id, filter);
            const existingIndex = prev.findIndex((item) => item.key === key);
            if (existingIndex > -1) {
                const updated = [...prev];
                updated[existingIndex] = { key, document_id, filter, response_data };
                return updated;
            }
            return [...prev, { key, document_id, filter, response_data }];
        });
        setVersion((v) => v + 1); // 每次更新遞增 version
    }, []);

    const getItem = useCallback(
        (document_id, filter = null) => {
            if (document_id == null) return undefined;
            const key = makeKey(document_id, filter);
            return tempData.find((item) => item.key === key);
        },
        [tempData, makeKey]
    );

    const removeItem = useCallback(
        (document_id, filter = null) => {
            if (document_id == null) {
                console.warn('[useGlobalTempStore] removeItem called with null/undefined document_id - ignored.');
                return;
            }
            const key = makeKey(document_id, filter);
            setTempData((prev) => prev.filter((item) => item.key !== key));
            setVersion((v) => v + 1); // 同樣更新 version
        },
        [makeKey]
    );

    const clear = useCallback(() => {
        setTempData([]);
        setVersion((v) => v + 1); // 清空也更新 version
    }, []);

    // keep track of active AbortControllers per composite key so we can cancel previous requests
    const controllersRef = useRef(new Map());
    // dedupe: track in-flight promises by composite key
    const inFlightRef = useRef(new Map());
    useEffect(() => {
        return () => {
            controllersRef.current.forEach((ctrl) => {
                try { ctrl.abort && ctrl.abort(); } catch (e) { }
            });
            controllersRef.current.clear();
            inFlightRef.current.clear();
        };
    }, []);

    const getOrFetchItem = useCallback(
        async (document_id, apiRoute = "/utility/response/cache", force = false, filter = null) => {
            if (document_id == null) {
                console.warn('[useGlobalTempStore] getOrFetchItem called with null/undefined document_id - returning null.');
                return null;
            }
            const key = makeKey(document_id, filter);

            if (!force) {
                const cached = tempData.find((item) => item.key === key);
                if (cached) return cached.response_data;
            }

            // coalesce: if same request is in-flight, await it instead of firing again
            const existingPromise = inFlightRef.current.get(key);
            if (existingPromise) {
                try {
                    return await existingPromise;
                } catch {
                    return null;
                }
            }

            // create a new in-flight request promise
            const controller = new AbortController();
            controllersRef.current.set(key, controller);

            const params = { document_id };
            if (filter != null) params.filter = stableStringify(filter);

            const fetchPromise = (async () => {
                try {
                    const res = await axios.get(apiRoute, { params, signal: controller.signal });
                    const data = res?.data?.data || res?.data || [];
                    setItem(document_id, data, filter);
                    return data;
                } catch (err) {
                    if (err && err.name === 'CanceledError') return null;
                    if (err && err.message && err.message.toLowerCase().includes('aborted')) return null;
                    console.error(`❌ 無法取得 document_id=${document_id} 的資料：`, err);
                    return null;
                } finally {
                    controllersRef.current.delete(key);
                    inFlightRef.current.delete(key);
                }
            })();

            inFlightRef.current.set(key, fetchPromise);
            return await fetchPromise;
        },
        [tempData, setItem, makeKey, stableStringify]
    );

    return (
        <TempDataContext.Provider
            value={{
                tempData,
                version, // 對外暴露 version
                setItem,
                getItem,
                removeItem,
                clear,
                getOrFetchItem,
                // helper for consumers who want to build keys themselves
                _makeKey: makeKey,
            }}
        >
            {children}
        </TempDataContext.Provider>
    );
};

// ✅ 安全模式 Hook：未包 Provider 時 fallback
export const useGlobalTempStore = () => {
    const context = useContext(TempDataContext);

    if (!context) {
        console.warn("[useGlobalTempStore] 未包覆 TempDataProvider，已使用安全模式 fallback。");
        return {
            tempData: [],
            version: 0, // ✅ fallback version
            setItem: (document_id, response_data, filter = null) => { },
            getItem: (document_id, filter = null) => undefined,
            removeItem: (document_id, filter = null) => { },
            clear: () => { },
            getOrFetchItem: async (document_id, apiRoute = "/utility/response/cache", force = false, filter = null) => null,
        };
    }

    return context;
};
